import {Box} from "@mui/material";
import React, {useContext, useState} from "react";
import ReactMarkdown, {Components} from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import {renderTemplateVariables} from "../shared/surveyMarkdownText.util";
import {SurveyRequiredMarker} from "../shared/SurveyQuestionMarkers";
import SurveyRunnerHelpButton from "./SurveyRunnerHelpButton";

interface Props {
    title: string
    required: boolean
    t: (key: string) => string
    /** 3 under a section heading (an h2), so screen readers read the page as an outline. */
    level?: 2 | 3
    /** The question's help in the guest's language; the (?) button shows only with one. */
    help?: string
}

/** The asterisk and the (?) after the title's last word. */
const TitleTail = React.createContext<React.ReactNode>(null);

/*
    Titles contain one paragraph. Keep its formatting and the tail in the same line flow. The renderers live at
    module level and read the tail from context: an inline renderer is a new component on every render, and React
    would remount the help button each time the card renders, closing its panel and dropping its focus.
*/
function TitleParagraph({children, style}: React.ComponentPropsWithoutRef<'p'>) {
    const tail = useContext(TitleTail);

    return (
        <Box component={'span'} style={style} sx={{display: 'block'}}>
            {children}
            {tail}
        </Box>
    );
}

function TitleLink({href, children}: React.ComponentPropsWithoutRef<'a'>) {
    return <a href={href} target={'_blank'} rel={'noopener noreferrer'}>{children}</a>;
}

const TITLE_COMPONENTS: Components = {p: TitleParagraph, a: TitleLink};

function SurveyRunnerQuestionTitle({title, required, t, level = 2, help}: Props) {
    // The help panel opens right after the heading, so its text never becomes part of the heading.
    const [helpSlot, setHelpSlot] = useState<HTMLDivElement | null>(null);

    // One no-wrap span, opened by a no-break space, keeps the markers on the line with the last word.
    const tail = (required || !!help) && (
        <Box component={'span'} sx={{whiteSpace: 'nowrap'}}>
            {'\u00a0'}
            {required && <SurveyRequiredMarker t={t}/>}
            {!!help && <SurveyRunnerHelpButton help={help} t={t} panelContainer={helpSlot}/>}
        </Box>
    );

    return (
        <>
            <Box
                component={level === 3 ? 'h3' : 'h2'}
                sx={{m: 0, fontSize: 24, fontWeight: 600, lineHeight: 1.4, overflowWrap: 'anywhere'}}
            >
                <TitleTail.Provider value={tail}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={TITLE_COMPONENTS}>
                        {renderTemplateVariables(title)}
                    </ReactMarkdown>
                </TitleTail.Provider>
            </Box>

            {!!help && <Box ref={setHelpSlot}/>}
        </>
    );
}

export default SurveyRunnerQuestionTitle;
